import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { authService } from '../services/authService';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const MembershipModal = ({ isOpen, onClose, onLoginSuccess }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('login'); // 'login' or 'register'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Login form state
    const [loginData, setLoginData] = useState({ email: '', phone: '' });

    // Register form state
    const [registerData, setRegisterData] = useState({ name: '', email: '', phone: '' });

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await authService.login(loginData.email, `+${loginData.phone}`);
            setSuccess(true);
            console.log('Login successful:', response.data);
            setTimeout(() => {
                if (onLoginSuccess) onLoginSuccess();
                onClose();
                navigate('/member'); // Use client-side navigation
            }, 1000);
        } catch (err) {
            setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
        } finally {
            setLoading(false);
        }
    };

    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await authService.register(
                registerData.name,
                registerData.email,
                `+${registerData.phone}`
            );
            setSuccess(true);
            console.log('Registration successful:', response.data);
            setTimeout(() => {
                if (onLoginSuccess) onLoginSuccess();
                onClose();
                navigate('/member'); // Use client-side navigation
            }, 1000);
        } catch (err) {
            const errorMsg = err.response?.data?.errors
                ? Object.values(err.response.data.errors).flat().join(', ')
                : err.response?.data?.message || 'Registration failed';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const switchTab = (tab) => {
        setActiveTab(tab);
        setError(null);
        setSuccess(false);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300">
            <div className="relative w-full max-w-md">
                {/* Close button */}
                <button
                    onClick={onClose}
                    className="absolute -top-10 right-0 text-white hover:text-gray-300 transition-colors"
                >
                    <X className="w-8 h-8" />
                </button>

                {/* Modal Card */}
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden min-h-[480px] flex flex-col">
                    {/* Tab Header */}
                    <div className="flex border-b border-gray-200 relative">
                        {/* Active tab indicator */}
                        <div
                            className="absolute bottom-0 h-1 transition-all duration-300 ease-in-out"
                            style={{
                                backgroundColor: '#31415A',
                                width: '50%',
                                transform: activeTab === 'login' ? 'translateX(0)' : 'translateX(100%)'
                            }}
                        />

                        <button
                            onClick={() => switchTab('login')}
                            className={`flex-1 py-4 text-center font-semibold transition-colors duration-300 ${activeTab === 'login'
                                ? 'text-[#31415A]'
                                : 'text-gray-400 hover:text-gray-600'
                                }`}
                        >
                            Sign In
                        </button>
                        <button
                            onClick={() => switchTab('register')}
                            className={`flex-1 py-4 text-center font-semibold transition-colors duration-300 ${activeTab === 'register'
                                ? 'text-[#31415A]'
                                : 'text-gray-400 hover:text-gray-600'
                                }`}
                        >
                            Join
                        </button>
                    </div>

                    {/* Content Area */}
                    <div className="p-8 flex-1">
                        {/* Common Messages */}
                        {success && (
                            <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm animate-fade-in">
                                ✓ {activeTab === 'login' ? 'Login' : 'Registration'} successful! Redirecting...
                            </div>
                        )}

                        {error && (
                            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm animate-fade-in">
                                {error}
                            </div>
                        )}

                        {/* Login Form */}
                        {activeTab === 'login' && (
                            <div className="animate-fade-in">
                                <h2 className="text-2xl font-bold mb-2" style={{ color: '#31415A' }}>
                                    Welcome Back
                                </h2>
                                <p className="text-gray-600 mb-6">Sign in to your membership account</p>

                                <form onSubmit={handleLoginSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            value={loginData.email}
                                            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="your@email.com"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Phone Number
                                        </label>
                                        <PhoneInput
                                            country={'id'}
                                            value={loginData.phone}
                                            onChange={phone => setLoginData({ ...loginData, phone })}
                                            inputStyle={{
                                                width: '100%',
                                                height: '48px',
                                                fontSize: '16px',
                                                paddingLeft: '48px',
                                                borderRadius: '0.5rem',
                                                border: '1px solid #d1d5db'
                                            }}
                                            buttonStyle={{
                                                borderRadius: '0.5rem 0 0 0.5rem',
                                                border: '1px solid #d1d5db',
                                                backgroundColor: '#f9fafb'
                                            }}
                                            dropdownStyle={{
                                                width: '300px'
                                            }}
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full py-3 px-4 rounded-lg text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                                        style={{ backgroundColor: '#31415A' }}
                                    >
                                        {loading ? 'Signing in...' : 'Sign In'}
                                    </button>
                                </form>
                            </div>
                        )}

                        {/* Register Form */}
                        {activeTab === 'register' && (
                            <div className="animate-fade-in">
                                <h2 className="text-2xl font-bold mb-2" style={{ color: '#31415A' }}>
                                    Join Membership
                                </h2>
                                <p className="text-gray-600 mb-6">Create your account and start earning rewards</p>

                                <form onSubmit={handleRegisterSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Full Name
                                        </label>
                                        <input
                                            type="text"
                                            value={registerData.name}
                                            onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                            placeholder="John Doe"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            value={registerData.email}
                                            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                                            placeholder="your@email.com"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Phone Number
                                        </label>
                                        <PhoneInput
                                            country={'id'}
                                            value={registerData.phone}
                                            onChange={phone => setRegisterData({ ...registerData, phone })}
                                            inputStyle={{
                                                width: '100%',
                                                height: '48px',
                                                fontSize: '16px',
                                                paddingLeft: '48px',
                                                borderRadius: '0.5rem',
                                                border: '1px solid #d1d5db'
                                            }}
                                            buttonStyle={{
                                                borderRadius: '0.5rem 0 0 0.5rem',
                                                border: '1px solid #d1d5db',
                                                backgroundColor: '#f9fafb'
                                            }}
                                            dropdownStyle={{
                                                width: '300px'
                                            }}
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full py-3 px-4 rounded-lg text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                                        style={{ backgroundColor: '#31415A' }}
                                    >
                                        {loading ? 'Creating...' : 'Join'}
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
        </div>
    );
};

export default MembershipModal;
